/**
 * SignedAgreement Entity Schemas
 *
 * A single immutable signed act: one row per (requirement, subject, signer-role).
 * Underlying Prisma model: `SignedAgreement` (apps/backend). The `mechanism`
 * discriminator selects how the signature was captured; `mechanismDetails` is a
 * discriminated union validated at write time. For `inline_canvas` the artifact
 * (signature bytes) lives in the forms-system FormSubmission referenced by
 * `formResponsePublicId`; the agreement row is the canonical identity/index, not
 * a second copy of the bytes. Signer identity fields are a write-time snapshot
 * and are authoritative for the compliance surface.
 *
 * The signer-role model supports one document requiring multiple signatures
 * (e.g. guardian AND student) — see `SignableRequirement.requiredSignerRoles`.
 */

import { z } from 'zod'

// --- Enums ---

export const SignerRoleSchema = z.enum(['guardian', 'student', 'staff', 'self'])
export type SignerRole = z.infer<typeof SignerRoleSchema>

export const AgreementMechanismSchema = z.enum(['inline_canvas', 'esign_envelope', 'acknowledgement'])
export type AgreementMechanism = z.infer<typeof AgreementMechanismSchema>

export const AgreementStatusSchema = z.enum(['pending', 'complete'])
export type AgreementStatus = z.infer<typeof AgreementStatusSchema>

/**
 * What a signature binds TO. `ticketed_event_admission` is the per-seat binding
 * (KTD14): a signature taken at order creation binds to the admission the
 * round-robin assigns, so a holder edit or a transfer keeps it valid and a
 * cancel or void leaves it in place (R44). `ticketed_event_attendee` stays for
 * compatibility — nothing re-points to it and no attendee-bound signature
 * exists to backfill. Kept in step with the `AgreementSubjectType` enum in
 * `apps/backend/prisma/schema/agreements.prisma`, which is what the column
 * accepts.
 */
export const AgreementSubjectTypeSchema = z.enum([
  'ticketed_event_attendee',
  'ticketed_event_order',
  'ticketed_event_admission'
])
export type AgreementSubjectType = z.infer<typeof AgreementSubjectTypeSchema>

// --- Mechanism payloads (discriminated union, validated at write) ---

export const InlineCanvasDetailsSchema = z.object({
  mechanism: z.literal('inline_canvas'),
  /** FormSubmission.publicId holding the signature artifact + schema snapshot. */
  formResponsePublicId: z.string().min(1)
})
export type InlineCanvasDetails = z.infer<typeof InlineCanvasDetailsSchema>

export const AcknowledgementDetailsSchema = z.object({
  mechanism: z.literal('acknowledgement'),
  /** The exact text the signer acknowledged (no separate artifact). */
  acknowledgedText: z.string().min(1)
})
export type AcknowledgementDetails = z.infer<typeof AcknowledgementDetailsSchema>

// `esign_envelope` is a DECLARED-BUT-UNBUILT mechanism slot. This shape is
// illustrative / non-binding; the real envelope model (recipient routing,
// multi-signer status, voided/declined lifecycle, completion certificate,
// webhook ids) is defined when the adapter is built. Reserving the discriminator
// costs nothing because the payload lives in JSON.
export const EsignEnvelopeDetailsSchema = z.object({
  mechanism: z.literal('esign_envelope'),
  provider: z.string().min(1),
  envelopeId: z.string().min(1),
  status: z.string().min(1)
})
export type EsignEnvelopeDetails = z.infer<typeof EsignEnvelopeDetailsSchema>

// EsignEnvelopeDetailsSchema is intentionally NOT in the live union yet — the
// e-sign adapter is unbuilt and its real shape is unknown, so accepting data
// against the placeholder would validate a shape we know is wrong. The
// `esign_envelope` DB enum value still reserves the discriminator; add the
// schema back to this union when the adapter ships.
export const MechanismDetailsSchema = z.discriminatedUnion('mechanism', [
  InlineCanvasDetailsSchema,
  AcknowledgementDetailsSchema
])
export type MechanismDetails = z.infer<typeof MechanismDetailsSchema>

// --- Entity (read DTO; excludes artifact bytes and internal ids) ---

export const SignedAgreementSchema = z.object({
  /** Agreement publicId. */
  id: z.string(),
  /** publicId of the SignableRequirement this satisfies. */
  requirementId: z.string(),
  subjectType: AgreementSubjectTypeSchema,
  subjectPublicId: z.string(),
  signerRole: SignerRoleSchema,
  /** Write-time snapshot of signer identity (authoritative for compliance). */
  signerName: z.string().nullable(),
  signerEmail: z.string().nullable(),
  signedAt: z.string().nullable(),
  documentVersion: z.string().nullable(),
  mechanism: AgreementMechanismSchema,
  status: AgreementStatusSchema,
  createdAt: z.string()
})
export type SignedAgreement = z.infer<typeof SignedAgreementSchema>

// --- Create input (service-facing) ---
// Agreements are created via the host flow (event order confirmation /
// before-attendance completion), not a public create endpoint this round.
export const CreateSignedAgreementSchema = z.object({
  requirementPublicId: z.string().min(1),
  subjectType: AgreementSubjectTypeSchema,
  subjectPublicId: z.string().min(1),
  signerRole: SignerRoleSchema,
  /**
   * UsersOnSchools publicId of the signer; school membership + role authority are
   * validated at write. Required: every agreement is attributed to a resolved
   * signer. Signer name/email are NOT accepted from the caller — they are
   * snapshotted from the resolved signer's User record so the immutable
   * compliance record cannot be forged.
   */
  signerPublicId: z.string().min(1),
  /**
   * What was agreed to. Required for `acknowledgement` (a content hash, since
   * there is no FormSubmission snapshot to pin it); derived from the submission
   * snapshot for `inline_canvas`.
   */
  documentVersion: z.string().min(1).nullable().optional(),
  mechanismDetails: MechanismDetailsSchema
})
export type CreateSignedAgreementInput = z.infer<typeof CreateSignedAgreementSchema>
